import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Button } from '@/components/ui/Button';
import { BowArc } from '@/components/ui/BowArc';
import { Display, Eyebrow } from '@/components/ui/Typography';
import {
  pushPermissionStatus,
  requestPushPermissionAndRegister
} from '@/lib/push';
import { useUserPosition } from '@/hooks/useUserPosition';
import { markPermissionsOnboardingDone } from '@/lib/permissions';
import { checkLocationState } from '@/lib/location';

type StepId = 'notifications' | 'location';

interface StepDef {
  id: StepId;
  icon: string;
  title: string;
  body: string;
  cta: string;
}

const STEPS: Record<StepId, StepDef> = {
  notifications: {
    id: 'notifications',
    icon: '🔔',
    title: 'Saiba na hora que sua recarga terminar',
    body:
      'Avisamos quando a recarga concluir e quando seu saldo Bow estiver ' +
      'baixo — mesmo com o app fechado. Nada de propaganda.',
    cta: 'Ativar notificações'
  },
  location: {
    id: 'location',
    icon: '📍',
    title: 'Veja o eletroposto mais perto de você',
    body:
      'Com sua localização, mostramos o eletroposto onde você está e ' +
      'evitamos iniciar a recarga no lugar errado. Usamos só com o app ' +
      'aberto — nunca em segundo plano.',
    cta: 'Usar minha localização'
  }
};

/**
 * Onboarding único de permissões — roda UMA vez por instalação, só no app
 * nativo, logo após o primeiro boot logado (gatilho no RequireAuth quando
 * pathname === '/').
 *
 * Regras (plano 2026-07-04):
 *  - Cada passo auto-skipa se a permissão já foi decidida (granted OU denied):
 *    o prompt nativo do iOS só existe uma vez, então nunca re-promptamos.
 *  - "Agora não" avança SEM tocar na API nativa (não queima o prompt). A
 *    localização volta a ser pedida pelo aviso da Home, que aparece a cada
 *    abertura enquanto ela não estiver ativa (2026-10-08; o card
 *    "Permissões" do Perfil saiu).
 *  - Sem passos pendentes → marca a flag e segue pra Home sem piscar tela.
 */
export default function PermissionsOnboarding() {
  const navigate = useNavigate();
  const position = useUserPosition();
  const [pending, setPending] = useState<StepId[] | null>(null);
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);

  // Monta a lista de passos pendentes UMA única vez (consulta direta às
  // permissões — não usa o status assíncrono do hook pra evitar race que
  // rebuildaria a lista no meio do fluxo).
  useEffect(() => {
    if (pending !== null) return;
    let cancelled = false;
    (async () => {
      if (!Capacitor.isNativePlatform()) {
        // Web nunca deveria chegar aqui (gatilho é nativo-only) — sai limpo.
        markPermissionsOnboardingDone();
        navigate('/', { replace: true });
        return;
      }
      const steps: StepId[] = [];
      const push = await pushPermissionStatus();
      if (push === 'prompt') steps.push('notifications');
      // GPS desligado também é passo pendente no Android: o botão abre o
      // diálogo nativo "Ativar localização". Antes o plugin REJEITAVA a
      // consulta nesse caso e o passo sumia (bug 2026-10-08). No iPhone com
      // GPS desligado não há diálogo — fica pro aviso da Home (Ajustes).
      const geo = await checkLocationState();
      if (geo === 'prompt' || (geo === 'off' && Capacitor.getPlatform() === 'android')) {
        steps.push('location');
      }
      if (cancelled) return;
      if (steps.length === 0) {
        markPermissionsOnboardingDone();
        navigate('/', { replace: true });
        return;
      }
      setPending(steps);
    })();
    return () => {
      cancelled = true;
    };
  }, [pending, navigate]);

  function advance() {
    if (!pending) return;
    if (index + 1 >= pending.length) {
      markPermissionsOnboardingDone();
      navigate('/', { replace: true });
    } else {
      setIndex(index + 1);
    }
  }

  async function activate(step: StepId) {
    setBusy(true);
    try {
      if (step === 'notifications') {
        await requestPushPermissionAndRegister();
      } else {
        await position.request();
      }
    } finally {
      setBusy(false);
      // Concedida ou negada, seguimos — não insistimos no mesmo prompt.
      advance();
    }
  }

  const step = useMemo(
    () => (pending ? STEPS[pending[index]] : null),
    [pending, index]
  );

  if (!step) {
    return (
      <div className="flex h-[100dvh] items-center justify-center bg-bow-cream">
        <BowArc size={44} variant="gradient" spin />
      </div>
    );
  }

  return (
    <div
      className="mx-auto flex max-w-sm flex-col overflow-hidden bg-bow-cream px-7"
      style={{
        height: '100dvh',
        paddingTop: 'max(2rem, env(safe-area-inset-top))',
        paddingBottom: 'max(1.75rem, env(safe-area-inset-bottom))'
      }}
    >
      <div className="flex flex-shrink-0 items-center justify-between">
        <div className="flex items-center gap-2.5">
          <BowArc size={28} />
          <span className="text-lg font-semibold tracking-tighter">Bow</span>
        </div>
        {pending && pending.length > 1 && (
          <span className="text-[12px] font-medium text-bow-royal/50">
            {index + 1} de {pending.length}
          </span>
        )}
      </div>

      {/* Miolo centrado — priming com valor antes do prompt do sistema */}
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-5 text-center">
        <span
          className="flex h-20 w-20 items-center justify-center rounded-full bg-white text-4xl shadow-soft"
          aria-hidden
        >
          {step.icon}
        </span>
        <div>
          <Eyebrow>Antes de começar</Eyebrow>
          <Display
            size={28}
            className="mx-auto mt-2 max-w-[300px] leading-tight [@media(max-height:740px)]:!text-[24px]"
          >
            {step.title}
          </Display>
          <p className="mx-auto mt-3 max-w-[300px] text-sm leading-relaxed text-bow-royal/60">
            {step.body}
          </p>
        </div>
      </div>

      {/* Ações ancoradas */}
      <div className="flex flex-shrink-0 flex-col gap-2">
        <Button onClick={() => void activate(step.id)} disabled={busy}>
          {busy ? 'Aguarde…' : step.cta}
        </Button>
        <Button variant="ghost" size="md" onClick={advance} disabled={busy}>
          Agora não
        </Button>
      </div>
    </div>
  );
}
